import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Package, Eye, Calendar, Clock, Loader2, RotateCcw, Sparkles, MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useSiteTexts } from "../lib/useSiteTexts";
import { TrialCalendarOptions } from "../components/TrialCalendarOptions";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  confirmed: "bg-[#4A7C59]/10 text-[#4A7C59]",
  active: "bg-[#9A7B4F]/10 text-[#9A7B4F]",
  completed: "bg-[#282828]/10 text-[#282828]",
  cancelled: "bg-red-50 text-red-600",
  pending: "bg-amber-50 text-amber-700",
};

export default function CustomerBookings() {
  const { customer, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const t = useSiteTexts();
  const [tab, setTab] = useState("rentals"); // "rentals" | "trials"
  const [bookings, setBookings] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleModal, setRescheduleModal] = useState(null); // { appt } or null
  const [cancelModal, setCancelModal] = useState(null);         // { appt } or null

  useEffect(() => {
    if (!authLoading && !customer) navigate("/login", { state: { from: "/account/bookings" }, replace: true });
    if (customer) fetchAll();
  }, [customer, authLoading, navigate]);

  const fetchAll = async () => {
    const token = localStorage.getItem("mahra_session_token");
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [rRentals, rTrials] = await Promise.all([
        fetch(`${API}/api/customer/bookings`, { headers }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/api/customer/appointments`, { headers }).then((r) => r.ok ? r.json() : { appointments: [] }),
      ]);
      setBookings(Array.isArray(rRentals) ? rRentals : []);
      // Trial Portal Page — show ONLY confirmed trials. Enquiry-only leads
      // live in the trial_leads collection and never appear here.
      const trialAppts = (rTrials?.appointments || []).filter((a) =>
        (a.appointment_type === "trial" || !a.appointment_type)
        && !["cancelled", "rejected", "no_show"].includes((a.status || "").toLowerCase())
      );
      setTrials(trialAppts);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !customer) return null;

  const current = bookings.filter((b) => ["confirmed", "active", "pending"].includes(b.booking_status));
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.booking_status));

  const todayIso = new Date().toISOString().slice(0, 10);
  const upcomingTrials = trials.filter((a) => (a.date || "9999-12-31") >= todayIso);
  const pastTrials = trials.filter((a) => (a.date || "0000-01-01") < todayIso);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <Link to="/account" className="flex items-center gap-1.5 text-[11px] text-[#676664]/50 tracking-[0.1em] uppercase mb-6 hover:text-[#676664]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Account
        </Link>
        <h1 className="text-2xl text-[#282828] font-light mb-6" data-testid="bookings-title">
          {t("portal_bookings_title", "My Bookings")}
        </h1>

        {/* Trial Portal — tabs */}
        <div className="flex gap-1 mb-8 border-b border-[#282828]/10">
          <button
            onClick={() => setTab("rentals")}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${
              tab === "rentals" ? "border-b-2 border-[#9A7B4F] text-[#3D2B1F]" : "text-[#676664]/60 hover:text-[#3D2B1F]"
            }`}
            data-testid="tab-rentals"
          >
            Rental Bookings {bookings.length > 0 && <span className="ml-1 opacity-60">({bookings.length})</span>}
          </button>
          <button
            onClick={() => setTab("trials")}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${
              tab === "trials" ? "border-b-2 border-[#9A7B4F] text-[#3D2B1F]" : "text-[#676664]/60 hover:text-[#3D2B1F]"
            }`}
            data-testid="tab-trials"
          >
            Trial Bookings {trials.length > 0 && <span className="ml-1 opacity-60">({trials.length})</span>}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
        ) : tab === "rentals" ? (
          bookings.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#282828]/8" data-testid="rentals-empty">
              <Package className="w-10 h-10 text-[#676664]/20 mx-auto mb-4" strokeWidth={1} />
              <p className="text-[#676664]/50 text-sm font-light mb-4">{t("portal_bookings_empty_heading", "No bookings yet")}</p>
              <Link to="/catalog" className="text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase hover:underline">
                {t("portal_bookings_empty_cta", "Browse Collection")}
              </Link>
            </div>
          ) : (
            <div className="space-y-10">
              {current.length > 0 && (
                <div>
                  <h2 className="text-[10px] text-[#676664]/50 tracking-[0.2em] uppercase mb-4">Current Bookings</h2>
                  <div className="space-y-3">{current.map((b) => <BookingCard key={b.id} booking={b} />)}</div>
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <h2 className="text-[10px] text-[#676664]/50 tracking-[0.2em] uppercase mb-4">Past Bookings</h2>
                  <div className="space-y-3">{past.map((b) => <BookingCard key={b.id} booking={b} />)}</div>
                </div>
              )}
            </div>
          )
        ) : (
          // ─── TRIALS TAB ────────────────────────────────────────
          trials.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#282828]/8" data-testid="trials-empty">
              <Sparkles className="w-10 h-10 text-[#676664]/20 mx-auto mb-4" strokeWidth={1} />
              <p className="text-[#676664]/60 text-sm font-light mb-2">{t("portal_trials_empty_heading", "No trial bookings yet")}</p>
              <p className="text-[#676664]/50 text-xs mb-4">{t("portal_trials_empty_body", "When you book a trial fitting, it'll appear here.")}</p>
              <Link to="/booking/trial" className="text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase hover:underline">
                {t("portal_trials_empty_cta", "Book a Trial")}
              </Link>
            </div>
          ) : (
            <div className="space-y-10">
              {upcomingTrials.length > 0 && (
                <div>
                  <h2 className="text-[10px] text-[#676664]/50 tracking-[0.2em] uppercase mb-4">Upcoming Trials</h2>
                  <div className="space-y-4">{upcomingTrials.map((a) => <TrialCard key={a.id} appt={a} upcoming onReschedule={() => setRescheduleModal({ appt: a })} onCancel={() => setCancelModal({ appt: a })} />)}</div>
                </div>
              )}
              {pastTrials.length > 0 && (
                <div>
                  <h2 className="text-[10px] text-[#676664]/50 tracking-[0.2em] uppercase mb-4">Past Trials</h2>
                  <div className="space-y-4">{pastTrials.map((a) => <TrialCard key={a.id} appt={a} upcoming={false} />)}</div>
                </div>
              )}
            </div>
          )
        )}
      </div>
      {rescheduleModal && (
        <TrialRescheduleModal
          appt={rescheduleModal.appt}
          onClose={() => setRescheduleModal(null)}
          onDone={() => { setRescheduleModal(null); fetchAll(); }}
        />
      )}
      {cancelModal && (
        <TrialCancelModal
          appt={cancelModal.appt}
          onClose={() => setCancelModal(null)}
          onDone={() => { setCancelModal(null); fetchAll(); }}
        />
      )}
      <Footer />
    </div>
  );
}

function BookingCard({ booking }) {
  const statusClass = statusColors[booking.booking_status] || statusColors.pending;
  const eventDate = booking.event_date ? new Date(booking.event_date + "T00:00:00") : null;
  return (
    <div className="bg-white border border-[#282828]/8 p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`booking-${booking.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[13px] text-[#282828] font-medium truncate">{booking.outfit_name}</p>
          <span className={`text-[9px] tracking-[0.1em] uppercase px-2 py-0.5 ${statusClass}`}>{booking.booking_status}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#676664]/60">
          {eventDate && (<span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {format(eventDate, "MMM d, yyyy")}</span>)}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {booking.rental_days} day rental</span>
          <span>${booking.total_amount?.toFixed(2)}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {["completed", "cancelled"].includes(booking.booking_status) && booking.outfit_id && (
          <Link to={`/outfit/${booking.outfit_id}`} className="flex items-center gap-1.5 text-[11px] text-[#282828]/50 tracking-[0.1em] uppercase hover:text-[#9A7B4F] transition-colors" data-testid={`rebook-${booking.id}`}>
            <RotateCcw className="w-3.5 h-3.5" /> Re-book
          </Link>
        )}
        <Link to={`/account/bookings/${booking.id}`} className="flex items-center gap-1.5 text-[11px] text-[#9A7B4F] tracking-[0.1em] uppercase hover:underline flex-shrink-0" data-testid={`view-booking-${booking.id}`}>
          <Eye className="w-3.5 h-3.5" /> View Details
        </Link>
      </div>
    </div>
  );
}

function TrialCard({ appt, upcoming, onReschedule, onCancel }) {
  const status = (appt.status || "confirmed").toLowerCase();
  const statusClass = statusColors[status] || statusColors.confirmed;
  const date = appt.date ? new Date(appt.date + "T00:00:00") : null;
  const details = {
    date: appt.date, startTime: appt.time,
    durationMinutes: appt.duration_minutes || 60,
    customerName: appt.customer_name, customerEmail: appt.customer_email,
  };

  // Trial Portal Patch 1.1 — 24-hour reschedule eligibility. Computed in
  // Australia/Melbourne so it matches the backend cutoff exactly.
  // Section 16 — the same >24h rule also gates the customer Cancel button.
  let canReschedule = false;
  if (upcoming && appt.date && appt.time) {
    try {
      const [y, m, d] = appt.date.split("-").map(Number);
      const [hh, mm] = appt.time.split(":").map(Number);
      // Melbourne offset from UTC: +10 or +11 (DST). Use naive parse via en-AU:
      const nowInMelbourne = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
      const apptInMelbourne = new Date(y, m - 1, d, hh, mm, 0);
      const hoursUntil = (apptInMelbourne - nowInMelbourne) / (1000 * 60 * 60);
      canReschedule = hoursUntil >= 24;
    } catch { canReschedule = false; }
  }

  return (
    <div className="bg-white border border-[#282828]/8 p-5" data-testid={`trial-${appt.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-[#9A7B4F]" />
            <p className="text-[13px] text-[#282828] font-medium">Trial Fitting</p>
            <span className={`text-[9px] tracking-[0.1em] uppercase px-2 py-0.5 ${statusClass}`}>{status}</span>
          </div>
          <div className="text-[11px] text-[#676664]/70 space-y-0.5">
            {date && <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {format(date, "EEEE, d MMMM yyyy")}</div>}
            {appt.time && <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {appt.time} · {details.durationMinutes} minutes (AEST)</div>}
            {appt.location && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {appt.location}</div>}
          </div>
        </div>
      </div>

      {upcoming && appt.date && appt.time && (
        <div className="pt-3 border-t border-[#282828]/8">
          <TrialCalendarOptions details={details} />
        </div>
      )}

      {upcoming && (
        <div className="pt-3 border-t border-[#282828]/8">
          {canReschedule ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-[11px] text-[#676664]/70 leading-relaxed flex-1">
                <strong className="text-[#3D2B1F]">Need to make changes?</strong> Trial bookings can be changed online up to <strong>24 hours</strong> before your appointment.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-[11px] tracking-[0.14em] uppercase bg-white border border-[#282828]/20 text-[#676664] hover:bg-[#282828]/5 transition-colors"
                  data-testid={`cancel-trial-${appt.id}`}
                >
                  <X className="w-3 h-3 inline mr-1.5" /> Cancel
                </button>
                <button
                  onClick={onReschedule}
                  className="px-4 py-2 text-[11px] tracking-[0.14em] uppercase bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white transition-colors"
                  data-testid={`reschedule-trial-${appt.id}`}
                >
                  <RotateCcw className="w-3 h-3 inline mr-1.5" /> Reschedule Trial
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-[#676664]/70 leading-relaxed" data-testid={`trial-under-24h-${appt.id}`}>
              <strong className="text-[#3D2B1F]">Need to make changes?</strong> Trial bookings can only be changed online up to <strong>24 hours</strong> before your appointment. Please <a href="mailto:hello@shopmahra.com.au" className="text-[#9A7B4F] hover:underline">contact Mahra</a> if you need help with a last-minute change.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Trial Portal Patch 1.1 — Reschedule modal ─────────────────────
function TrialRescheduleModal({ appt, onClose, onDone }) {
  const [monthDate, setMonthDate] = useState(() => new Date(appt.date + "T00:00:00"));
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const monthLabel = monthDate.toLocaleString("en-AU", { month: "long", year: "numeric" });

  useEffect(() => {
    setLoading(true);
    const fromDate = new Date(y, m, 1).toISOString().slice(0, 10);
    const toDate = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    fetch(`${API}/api/booking/trial-slots/available?from_date=${fromDate}&to_date=${toDate}`)
      .then((r) => r.ok ? r.json() : { slots: [] })
      .then((d) => {
        const byDate = {};
        (d.slots || []).forEach((s) => {
          (byDate[s.date] = byDate[s.date] || []).push(s);
        });
        setSlotsByDate(byDate);
      })
      .finally(() => setLoading(false));
  }, [y, m]);

  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const firstDay = new Date(y, m, 1).getDay(); // 0=Sun
  const gridStart = (firstDay + 6) % 7; // Mon-first
  const today = new Date().toISOString().slice(0, 10);

  const doSave = async () => {
    if (!selectedDate || !selectedTime) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("mahra_session_token");
      const r = await fetch(`${API}/api/customer/appointments/${appt.id}/reschedule`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date: selectedDate, time: selectedTime }),
      });
      if (r.ok) {
        toast.success("Trial rescheduled");
        onDone();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Failed to reschedule");
      }
    } finally { setSaving(false); }
  };

  const timeSlots = slotsByDate[selectedDate] || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="reschedule-modal">
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-[#282828]/8">
          <div>
            <h2 className="text-lg text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Reschedule Your Trial</h2>
            <p className="text-[11px] text-[#676664]/60 mt-1">Currently: {appt.date} at {appt.time}</p>
          </div>
          <button onClick={onClose} className="text-[#676664]/50 hover:text-[#282828]" data-testid="reschedule-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setMonthDate(new Date(y, m - 1, 1))} className="p-1 text-[#676664] hover:text-[#282828]" data-testid="reschedule-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-[13px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{monthLabel}</div>
            <button onClick={() => setMonthDate(new Date(y, m + 1, 1))} className="p-1 text-[#676664] hover:text-[#282828]" data-testid="reschedule-next-month">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 text-[9px] uppercase text-[#676664]/50 tracking-wider mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (<div key={d} className="text-center py-1">{d}</div>))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: gridStart }).map((_, i) => <div key={`b${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const daySlots = slotsByDate[dateStr] || [];
                  const isAvailable = daySlots.length > 0 && dateStr > today;
                  const isSelected = selectedDate === dateStr;
                  let cls = "aspect-square flex items-center justify-center text-[13px] rounded transition-colors";
                  if (!isAvailable) cls += " text-[#676664]/25 cursor-not-allowed";
                  else if (isSelected) cls += " bg-[#9A7B4F] text-white";
                  else cls += " bg-[#B8C9BD]/22 text-[#2F5D3B] hover:bg-[#B8C9BD]/40 cursor-pointer";
                  return (
                    <button key={day} disabled={!isAvailable}
                      onClick={() => { setSelectedDate(dateStr); setSelectedTime(""); }}
                      className={cls}
                      data-testid={isAvailable ? `reschedule-day-${dateStr}` : undefined}
                    >{day}</button>
                  );
                })}
              </div>
              {selectedDate && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-2">Available times on {selectedDate}</p>
                  <div className="flex flex-wrap gap-2">
                    {timeSlots.map((s) => {
                      // /booking/trial-slots/available returns `start_time`,
                      // not `time`. Fall back to `time` for defence-in-depth.
                      const slotTime = s.start_time || s.time;
                      return (
                        <button key={slotTime} onClick={() => setSelectedTime(slotTime)}
                          className={`px-3 py-2 text-[12px] border transition-colors ${selectedTime === slotTime ? "bg-[#9A7B4F] border-[#9A7B4F] text-white" : "bg-white border-[#282828]/15 hover:border-[#9A7B4F]"}`}
                          data-testid={`reschedule-time-${slotTime}`}
                        >{slotTime}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          <button
            onClick={doSave}
            disabled={!selectedDate || !selectedTime || saving}
            className="w-full mt-6 py-3 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[12px] tracking-[0.14em] uppercase transition-colors disabled:opacity-40"
            data-testid="reschedule-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 inline animate-spin" /> : "Confirm New Time"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section 16 — Cancel Trial confirmation modal ─────────────────────
const CANCEL_REASONS = [
  { value: "change_of_plans",      label: "Change of plans" },
  { value: "illness",              label: "Illness" },
  { value: "rebooking",            label: "Rebooking for another date" },
  { value: "found_another_outfit", label: "Found another outfit" },
  { value: "no_longer_attending",  label: "No longer attending the event" },
  { value: "other",                label: "Other" },
  { value: "prefer_not_to_say",    label: "Prefer not to say" },
];

function TrialCancelModal({ appt, onClose, onDone }) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Section 16 reason capture — both fields optional, cancellation never blocks.
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");

  const submit = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    const token = localStorage.getItem("mahra_session_token");
    const payload = {};
    if (reason) payload.cancellation_reason = reason;
    if (reason === "other" && reasonOther.trim()) payload.cancellation_reason_other = reasonOther.trim();
    try {
      const r = await fetch(`${API}/api/customer/appointments/${appt.id}/cancel`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.already_cancelled) {
          toast.info("This trial was already cancelled.");
        } else {
          toast.success("Trial cancelled. A confirmation has been sent.");
        }
        onDone?.();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Failed to cancel");
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.message || "Network error");
      setSubmitting(false);
    }
  };

  const date = appt.date ? new Date(appt.date + "T00:00:00") : null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-trial-modal">
      <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl text-[#282828] font-light">Cancel your trial fitting?</h3>
            <p className="text-[11px] text-[#676664]/60 mt-1">This can&apos;t be undone from here.</p>
          </div>
          <button onClick={onClose} className="text-[#676664]/50 hover:text-[#282828]" data-testid="cancel-trial-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-[#FAF8F0] border border-[#282828]/10 p-4 mb-4">
          <p className="text-[11px] text-[#676664]/80 mb-1 uppercase tracking-[0.1em]">You&apos;re cancelling</p>
          <p className="text-[13px] text-[#282828] font-medium">
            {date ? format(date, "EEEE, d MMMM yyyy") : appt.date} {appt.time && `· ${appt.time}`}
          </p>
        </div>
        <ul className="text-[11px] text-[#676664]/80 leading-relaxed space-y-1.5 mb-4 list-disc pl-4">
          <li>The slot will be released immediately for other customers.</li>
          <li>You&apos;ll receive an email and SMS confirmation.</li>
          <li>If you&apos;d like a different time, you can rebook straight away.</li>
        </ul>

        {/* Section 16 — optional cancellation reason. Never blocks submit. */}
        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-[0.14em] text-[#676664]/70 mb-1.5" htmlFor="cancel-reason">
            Reason <span className="normal-case tracking-normal text-[#676664]/50">(optional)</span>
          </label>
          <select
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] bg-white"
            data-testid="cancel-trial-reason-select"
          >
            <option value="">— Prefer not to specify —</option>
            {CANCEL_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {reason === "other" && (
            <input
              type="text"
              value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value.slice(0, 200))}
              placeholder="Briefly (optional)"
              maxLength={200}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] mt-2"
              data-testid="cancel-trial-reason-other"
            />
          )}
        </div>

        <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="cancel-trial-confirm-wrap">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="accent-[#9A7B4F] mt-1"
            data-testid="cancel-trial-confirm-checkbox"
          />
          <span className="text-[11px] text-[#3D2B1F]">Yes, I&apos;d like to cancel this trial fitting.</span>
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-[11px] tracking-[0.14em] uppercase border border-[#282828]/20 text-[#676664] hover:bg-[#282828]/5 disabled:opacity-50"
            data-testid="cancel-trial-abort"
          >
            Keep booking
          </button>
          <button
            onClick={submit}
            disabled={!confirmed || submitting}
            className="px-4 py-2 text-[11px] tracking-[0.14em] uppercase bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
            data-testid="cancel-trial-confirm-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : "Cancel trial"}
          </button>
        </div>
      </div>
    </div>
  );
}

